/* Runtime translation layer.
   Tier 1 (always in Hindi): dictionary lookups over every text node & common attributes —
   covers pages that have not been hand-wired with t().
   Tier 2 (data toggle): remaining English text (record data) is batch-translated through
   POST /api/i18n/translate (LLM + server cache) and swapped in place, reversibly. */
import i18n from "@/i18n";

const originals = new Map();      // TextNode -> original string
const lastSet = new WeakMap();    // TextNode -> value we wrote (loop guard)
const attrOriginals = new Map();  // Element -> { attr: original }
const aiCache = new Map();        // english -> hindi (session; server caches permanently)
const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "svg", "CODE", "PRE"]);

let observer = null;
let aiEnabled = false;
let walkTimer = null;
let aiTimer = null;
let pendingAI = new Set();
let apiPost = null;               // injected to avoid import cycles

const dict = (s) => {
  const hit = i18n.getResource("hi", "translation", s);
  return typeof hit === "string" ? hit : null;
};

const translatable = (s) => {
  if (s.length < 3 || s.length > 400) return false;
  if (!/[A-Za-z]{3}/.test(s)) return false;
  if (/@|https?:\/\//.test(s)) return false;
  if (/[\u0900-\u097F]/.test(s)) return false;
  return true;
};

const setText = (node, value) => {
  const raw = node.nodeValue;
  const lead = raw.match(/^\s*/)[0];
  const tail = raw.match(/\s*$/)[0];
  const next = lead + value + tail;
  if (raw === next) return;
  if (!originals.has(node)) originals.set(node, raw);
  lastSet.set(node, next);
  node.nodeValue = next;
};

const handleTextNode = (node) => {
  if (node.nodeValue === lastSet.get(node)) return;
  const s = node.nodeValue.trim();
  if (!s) return;
  const hit = dict(s);
  if (hit) { setText(node, hit); return; }
  if (!aiEnabled || !translatable(s)) return;
  if (aiCache.has(s)) { setText(node, aiCache.get(s)); return; }
  pendingAI.add(s);
  scheduleAI();
};

const handleElement = (el) => {
  for (const attr of ATTRS) {
    const v = el.getAttribute && el.getAttribute(attr);
    if (!v) continue;
    const hit = dict(v.trim()) || (aiEnabled ? aiCache.get(v.trim()) : null);
    if (hit && hit !== v) {
      const store = attrOriginals.get(el) || {};
      if (!(attr in store)) { store[attr] = v; attrOriginals.set(el, store); }
      el.setAttribute(attr, hit);
    }
  }
};

const walk = () => {
  const root = document.getElementById("root");
  if (!root || i18n.language !== "hi") return;
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) =>
      (n.nodeType === Node.ELEMENT_NODE && SKIP.has(n.nodeName))
        ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  let n;
  while ((n = tw.nextNode())) {
    if (n.nodeType === Node.TEXT_NODE) handleTextNode(n);
    else handleElement(n);
  }
};

const scheduleWalk = () => {
  clearTimeout(walkTimer);
  walkTimer = setTimeout(walk, 120);
};

const scheduleAI = () => {
  clearTimeout(aiTimer);
  aiTimer = setTimeout(async () => {
    const batch = [...pendingAI].slice(0, 80);
    pendingAI = new Set([...pendingAI].slice(80));
    if (!batch.length || !apiPost) return;
    try {
      const { data } = await apiPost("/i18n/translate", { texts: batch });
      Object.entries(data.translations || {}).forEach(([en, hi]) => aiCache.set(en, hi));
      scheduleWalk();
    } catch (e) { /* translation is best-effort */ }
    if (pendingAI.size) scheduleAI();
  }, 500);
};

export const startDomTranslation = (withAI, post) => {
  aiEnabled = !!withAI;
  apiPost = post || apiPost;
  if (!observer) {
    observer = new MutationObserver(scheduleWalk);
    observer.observe(document.getElementById("root"), {
      childList: true, subtree: true, characterData: true,
    });
  }
  scheduleWalk();
};

export const stopDomTranslation = () => {
  if (observer) { observer.disconnect(); observer = null; }
  clearTimeout(walkTimer);
  clearTimeout(aiTimer);
  pendingAI = new Set();
  originals.forEach((value, node) => { if (node.isConnected) node.nodeValue = value; });
  originals.clear();
  attrOriginals.forEach((store, el) => {
    if (!el.isConnected) return;
    Object.entries(store).forEach(([attr, value]) => el.setAttribute(attr, value));
  });
  attrOriginals.clear();
};

export const setDataTranslation = (enabled) => {
  aiEnabled = !!enabled;
  if (!enabled) {
    // drop AI-made swaps but keep dictionary ones: restore all, re-walk with dict only
    originals.forEach((value, node) => { if (node.isConnected) node.nodeValue = value; });
    originals.clear();
  }
  scheduleWalk();
};
